/**
 * Форма входа. Наша целиком: своё название, свой текст, свои ошибки.
 * Интерфейс Traccar в этом не участвует.
 */

import { useState, type FormEvent } from 'react'

import { ApiError, api, type Identity } from './api'
import { BackgroundMap } from './BackgroundMap'
import { LangPicker } from './LangPicker'
import { TruckIcon } from './icons'
import { dict, type Lang } from './i18n'

type Props = {
  lang: Lang
  onLang: (lang: Lang) => void
  center: { lat: number; lon: number }
  onSignedIn: (identity: Identity) => void
}

/** Ключ памяти браузера: входил ли отсюда человек с кодом. */
const TWO_STEP_SEEN = 'telematic.twoStep'

function remembersTwoStep(): boolean {
  try {
    return localStorage.getItem(TWO_STEP_SEEN) === '1'
  } catch {
    return false // приватный режим или закрытое хранилище — не беда
  }
}

function rememberTwoStep(on: boolean): void {
  try {
    if (on) localStorage.setItem(TWO_STEP_SEEN, '1')
    else localStorage.removeItem(TWO_STEP_SEEN)
  } catch {
    // выбор проживёт до конца сессии — этого достаточно
  }
}

export function Login({ lang, onLang, center, onSignedIn }: Props) {
  const t = dict(lang)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Поле кода не показывается само. Показывать его после неудачного входа
  // было ошибкой: опечатка в пароле выглядела как требование кода, которого у
  // человека нет. Отличить одно от другого нельзя — Traccar отвечает на оба
  // случая одинаковым 401 и различает их только номером строки в стектрейсе,
  // а это сдвинется при первом же его обновлении.
  //
  // Поэтому поле открывает сам человек, а браузер запоминает его выбор: тот,
  // кто входит с кодом, увидит поле сразу и в следующий раз.
  const [code, setCode] = useState('')
  const [askCode, setAskCode] = useState(() => remembersTwoStep())

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const identity = await api.login(email, password, code)
      rememberTwoStep(identity.twoStep)
      onSignedIn(identity)
    } catch (caught) {
      // Прослойка уже отдала текст, пригодный для человека. Пустой текст
      // означает, что до сервера не дошли вовсе.
      const message =
        caught instanceof ApiError && caught.status !== 0 && caught.message
          ? caught.message
          : t.networkError
      setError(message)
      setBusy(false)
    }
  }

  return (
    <div className="login-screen">
      <BackgroundMap center={center} />

      <form className="login-card" onSubmit={submit}>
        <div className="brand">
          <span className="brand-mark">
            <TruckIcon />
          </span>
          <span className="brand-text">
            <h1>{t.appName}</h1>
            <p className="sub">{t.signInSub}</p>
          </span>
        </div>

        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        <label className="field">
          <span>{t.email}</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span>{t.password}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {askCode && (
          <label className="field">
            <span>{t.twoStepCode}</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123 456"
            />
          </label>
        )}

        {!askCode && (
          <button type="button" className="link" onClick={() => setAskCode(true)}>
            {t.twoStepHave}
          </button>
        )}

        <button className="primary" type="submit" disabled={busy}>
          {busy ? t.signingIn : t.signInAction}
        </button>
      </form>

      <LangPicker lang={lang} onLang={onLang} />
    </div>
  )
}
